import React, { useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet-async";
import { useNavigate, useParams } from "react-router-dom";
import BracketBoard from "../components/brackets/BracketBoard";
import PublicHeader from "../components/PublicHeader";
import { getAllCompetitions } from "../firebase/competitions";
import {
  listenPublicDraw,
  listenPublicDrawIndex,
  PublicDrawIndexItem,
  PublicTournamentDraw,
} from "../firebase/tournamentDraws";
import { useTranslation } from "../hooks/useTranslation";
import { formatDate } from "../utils/dateHelpers";
import "./PublicCompetitionDraws.css";

function medalists(draw: PublicTournamentDraw["draw"]) {
  const final = draw.rounds[draw.rounds.length - 1]?.[0];
  if (!final?.winnerEntrantId || !final.redEntrantId || !final.blueEntrantId) return null;
  const silver = final.winnerEntrantId === final.redEntrantId ? final.blueEntrantId : final.redEntrantId;
  const semifinal = draw.rounds[draw.rounds.length - 2] ?? [];
  const bronze = semifinal.flatMap((match) => {
    if (!match.winnerEntrantId || !match.redEntrantId || !match.blueEntrantId) return [];
    return [match.winnerEntrantId === match.redEntrantId ? match.blueEntrantId : match.redEntrantId];
  });
  return { gold: final.winnerEntrantId, silver, bronze };
}

export default function PublicCompetitionDraws() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t, language } = useTranslation();
  const [competition, setCompetition] = useState<any | null>(null);
  const [drawIndex, setDrawIndex] = useState<PublicDrawIndexItem[]>([]);
  const [selected, setSelected] = useState<PublicTournamentDraw | null>(null);
  const [selectedId, setSelectedId] = useState("");
  const [indexLoading, setIndexLoading] = useState(true);
  const [drawLoading, setDrawLoading] = useState(false);
  const [error, setError] = useState("");
  const [displayMode, setDisplayMode] = useState(false);

  useEffect(() => {
    if (!id) return;
    let active = true;
    getAllCompetitions().then((items) => {
      if (active) setCompetition((items as any[]).find((item) => item.id === id) ?? null);
    }).catch((loadError) => console.error("Public draw competition load failed", loadError));
    const unsubscribe = listenPublicDrawIndex(id, (items) => {
      if (!active) return;
      setDrawIndex(items);
      setSelectedId((current) => items.some((item) => item.drawId === current) ? current : items[0]?.drawId ?? "");
      setIndexLoading(false);
      setError("");
    }, (listenError) => {
      console.error("Public draw listener failed", listenError);
      if (active) { setError(t("publicDraws.error")); setIndexLoading(false); }
    });
    return () => { active = false; unsubscribe(); };
  }, [id, t]);

  useEffect(() => {
    if (!selectedId) {
      setSelected(null);
      setDrawLoading(false);
      return;
    }
    setDrawLoading(true);
    let active = true;
    const unsubscribe = listenPublicDraw(selectedId, (draw) => {
      if (!active) return;
      setSelected(draw);
      setError("");
      setDrawLoading(false);
    }, (listenError) => {
      console.error("Selected public draw listener failed", listenError);
      if (active) { setError(t("publicDraws.error")); setDrawLoading(false); }
    });
    return () => { active = false; unsubscribe(); };
  }, [selectedId, t]);

  useEffect(() => {
    const onFullscreenChange = () => setDisplayMode(!!document.fullscreenElement);
    document.addEventListener("fullscreenchange", onFullscreenChange);
    return () => document.removeEventListener("fullscreenchange", onFullscreenChange);
  }, []);

  const podium = selected ? medalists(selected.draw) : null;
  const loading = indexLoading || (drawIndex.length > 0 && drawLoading);
  const labels = useMemo(() => ({
    round: t("drawLab.round"), semifinal: t("drawLab.semifinal"), final: t("drawLab.final"),
    fight: t("drawLab.fight"), bye: t("drawLab.bye"), waiting: t("drawLab.waiting"),
    seed: t("drawLab.seed"), winner: t("drawLab.winner"), undo: t("drawLab.undo"),
  }), [t]);

  const toggleFullscreen = async () => {
    const target = document.getElementById("public-draw-stage");
    if (!target) return;
    try {
      if (document.fullscreenElement) await document.exitFullscreen();
      else await target.requestFullscreen();
    } catch (fullscreenError) {
      console.warn("Fullscreen is unavailable", fullscreenError);
      setDisplayMode((current) => !current);
    }
  };

  return (
    <div className={`public-draw-page ${displayMode ? "is-display" : ""}`}>
      <Helmet><title>{competition?.name ? `${competition.name} — ${t("publicDraws.title")}` : t("publicDraws.title")}</title></Helmet>
      {!displayMode && <PublicHeader backTo="/events" backLabel={t("nav.backToEvents")} />}
      <main id="public-draw-stage" className="public-draw-main">
        <div className="public-draw-title-row">
          <div><p>{t("publicDraws.eyebrow")}</p><h1>{competition?.name || t("publicDraws.title")}</h1>
            {competition?.startDate && <span>{formatDate(competition.startDate, language)}</span>}</div>
          <button onClick={toggleFullscreen}>{displayMode ? t("publicDraws.exitDisplay") : t("publicDraws.displayMode")}</button>
        </div>
        {!displayMode && <nav className="public-competition-tabs" aria-label={t("publicDraws.navigation")}>
          <button onClick={() => navigate(`/competition/${id}/registrations`)}>{t("publicDraws.registrationsTab")}</button>
          <button className="active">{t("publicDraws.drawTab")}</button>
        </nav>}

        {loading && <div className="public-draw-state">{t("publicDraws.loading")}</div>}
        {error && <div className="public-draw-state error">{error}</div>}
        {!loading && !error && drawIndex.length === 0 && <div className="public-draw-state"><strong>{t("publicDraws.notPublished")}</strong><span>{t("publicDraws.notPublishedHelp")}</span></div>}

        {drawIndex.length > 0 && <>
          <div className="public-draw-categories">
            {drawIndex.map((item) => <button className={item.drawId === selectedId ? "active" : ""} key={item.drawId} onClick={() => setSelectedId(item.drawId)}>
              <strong>{item.category}</strong><span>{item.division} · {item.weightCategory}</span>
            </button>)}
          </div>
          {selected && <section className="public-draw-card">
            <header><div><p>{selected.status === "LIVE" ? t("publicDraws.live") : selected.status}</p><h2>{selected.category} · {selected.division} · {selected.weightCategory}</h2></div>
              <div><span>{t("drawLab.version")} {selected.version}</span><span>{selected.draw.bracketSize} {t("publicDraws.slots")}</span></div></header>
            <BracketBoard draw={selected.draw} labels={labels} />
          </section>}
          {selected && podium && <section className="public-draw-podium">
            <article><span>1</span><strong>{selected.draw.entrants[podium.gold]?.name}</strong><small>{t("drawLab.gold")}</small></article>
            <article><span>2</span><strong>{selected.draw.entrants[podium.silver]?.name}</strong><small>{t("drawLab.silver")}</small></article>
            <article><span>3</span><strong>{podium.bronze.map((entrantId) => selected.draw.entrants[entrantId]?.name).join(" · ")}</strong><small>{t("drawLab.bronze")}</small></article>
          </section>}
        </>}
      </main>
    </div>
  );
}
